import { useCallback, useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";

export interface StackItem {
  id: string;
  node: React.ReactNode;
}

// Movement before the gesture's intent (scroll the card vs. change card) is decided.
const SLOP = 8;
// Vertical travel that commits a card change.
const SWIPE_THRESHOLD = 56;
// How far the card is allowed to follow the finger.
const MAX_PULL = 90;

/** Nearest scrollable ancestor of `target` inside `root`, if any. */
function scrollerUnder(target: Element | null, root: Element | null): HTMLElement | null {
  let el: Element | null = target;
  while (el && el !== root) {
    if (el instanceof HTMLElement) {
      const overflowY = getComputedStyle(el).overflowY;
      if ((overflowY === "auto" || overflowY === "scroll") && el.scrollHeight > el.clientHeight + 1) return el;
    }
    el = el.parentElement;
  }
  return null;
}

function isEditable(target: EventTarget | null): boolean {
  const el = target instanceof Element ? target.closest("input, textarea, [contenteditable]") : null;
  return el !== null;
}

/**
 * Vertical 3D card stack (adapted from the vertical-image-stack pattern for
 * content cards). Swipe / drag / scroll vertically or tap the dots to move
 * between cards. Ordered (clamped, not looping).
 *
 * Touch is handled here at the root instead of with framer's per-card `drag`.
 * The stack sits on a scrollable page, and framer only arms the current card —
 * so gestures starting on a peeking neighbour, or inside a card whose own
 * content overflows, were handed to the browser and scrolled the page instead
 * of moving the stack. The card area sets `touch-action: none` so every
 * vertical gesture arrives here, and this component decides between scrolling
 * the card's own overflow and advancing to the next card.
 */
export function VerticalCardStack({
  items,
  cardWidth = 320,
  cardHeight = 460,
  className,
}: {
  items: StackItem[];
  cardWidth?: number;
  cardHeight?: number;
  className?: string;
}) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const lastNav = useRef(0);
  const cooldown = 360;

  const navigate = useCallback((dir: number) => {
    const now = Date.now();
    if (now - lastNav.current < cooldown) return;
    lastNav.current = now;
    setCurrentIndex((prev) => Math.min(Math.max(prev + dir, 0), items.length - 1));
  }, [items.length]);

  const rootRef = useRef<HTMLDivElement>(null);
  // The card area proper. Touch listeners live here, not on the root: this is
  // the element carrying `touch-action: none`, so the browser never starts a
  // page scroll inside it. The root's margins keep `touch-action: auto` so the
  // page still scrolls normally when dragged beside the stack.
  const cardAreaRef = useRef<HTMLDivElement>(null);
  // Wrapper on the current card that follows the finger; moved directly rather
  // than through state so a drag doesn't re-render the card's contents.
  const pullRef = useRef<HTMLDivElement | null>(null);

  const setPull = useCallback((px: number, settle = false) => {
    const el = pullRef.current;
    if (!el) return;
    el.style.transition = settle ? "transform 220ms cubic-bezier(0.22, 1, 0.36, 1)" : "none";
    el.style.transform = px ? `translate3d(0, ${px}px, 0)` : "";
  }, []);

  const gesture = useRef({
    active: false,
    startY: 0,
    startX: 0,
    lastY: 0,
    offset: 0,
    intent: null as null | "swipe" | "scroll" | "horizontal",
    scroller: null as HTMLElement | null,
  });

  const onGestureStart = useCallback((x: number, y: number, target: EventTarget | null) => {
    gesture.current = {
      active: true,
      startY: y,
      startX: x,
      lastY: y,
      offset: 0,
      intent: null,
      scroller: scrollerUnder(target instanceof Element ? target : null, cardAreaRef.current),
    };
  }, []);

  /** Returns true when the stack consumed the movement (caller suppresses the browser default). */
  const onGestureMove = useCallback((x: number, y: number): boolean => {
    const g = gesture.current;
    if (!g.active) return false;

    const dx = x - g.startX;
    const dy = y - g.startY;

    if (!g.intent) {
      if (Math.abs(dx) < SLOP && Math.abs(dy) < SLOP) return false;
      if (Math.abs(dx) > Math.abs(dy)) {
        g.intent = "horizontal";
        return false;
      }
      // A card whose own content still has room in this direction scrolls first;
      // only once it bottoms out does the gesture become a card change.
      const sc = g.scroller;
      if (sc) {
        const atTop = sc.scrollTop <= 0;
        const atBottom = sc.scrollTop + sc.clientHeight >= sc.scrollHeight - 1;
        const draggingUp = dy < 0;
        g.intent = (draggingUp && !atBottom) || (!draggingUp && !atTop) ? "scroll" : "swipe";
      } else {
        g.intent = "swipe";
      }
    }

    const step = y - g.lastY;
    g.lastY = y;

    if (g.intent === "scroll") {
      const sc = g.scroller;
      if (!sc) return false;
      sc.scrollTop -= step;
      const atTop = sc.scrollTop <= 0;
      const atBottom = sc.scrollTop + sc.clientHeight >= sc.scrollHeight - 1;
      if ((step < 0 && atBottom) || (step > 0 && atTop)) {
        // Hand the rest of the gesture over to the stack, re-anchored so the
        // card picks up from the finger's current position rather than jumping.
        g.intent = "swipe";
        g.startY = y;
      }
      return true;
    }

    if (g.intent === "swipe") {
      g.offset = y - g.startY;
      setPull(Math.max(-MAX_PULL, Math.min(MAX_PULL, g.offset * 0.35)));
      return true;
    }

    return false;
  }, [setPull]);

  const onGestureEnd = useCallback(() => {
    const g = gesture.current;
    if (!g.active) return;
    g.active = false;
    if (g.intent === "swipe") {
      if (g.offset <= -SWIPE_THRESHOLD) navigate(1);
      else if (g.offset >= SWIPE_THRESHOLD) navigate(-1);
    }
    setPull(0, true);
  }, [navigate, setPull]);

  // Touch: non-passive so the page scroll can be suppressed once the gesture is
  // ours. `touch-action: none` on this element keeps the browser from claiming
  // the gesture during the slop window, before the first preventDefault lands.
  useEffect(() => {
    const el = cardAreaRef.current;
    if (!el) return;
    const handleStart = (e: TouchEvent) => {
      const t = e.touches[0];
      if (t) onGestureStart(t.clientX, t.clientY, e.target);
    };
    const handleMove = (e: TouchEvent) => {
      const t = e.touches[0];
      if (!t) return;
      if (onGestureMove(t.clientX, t.clientY) && e.cancelable) e.preventDefault();
    };
    el.addEventListener("touchstart", handleStart, { passive: true });
    el.addEventListener("touchmove", handleMove, { passive: false });
    el.addEventListener("touchend", onGestureEnd, { passive: true });
    el.addEventListener("touchcancel", onGestureEnd, { passive: true });
    return () => {
      el.removeEventListener("touchstart", handleStart);
      el.removeEventListener("touchmove", handleMove);
      el.removeEventListener("touchend", onGestureEnd);
      el.removeEventListener("touchcancel", onGestureEnd);
    };
  }, [onGestureStart, onGestureMove, onGestureEnd]);

  // Mouse drag (desktop). Skipped on editable targets so text can still be
  // selected and edited with the pointer.
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (e.button !== 0 || isEditable(e.target)) return;
    onGestureStart(e.clientX, e.clientY, e.target);
    const move = (ev: MouseEvent) => onGestureMove(ev.clientX, ev.clientY);
    const up = () => {
      window.removeEventListener("mousemove", move);
      window.removeEventListener("mouseup", up);
      onGestureEnd();
    };
    window.addEventListener("mousemove", move);
    window.addEventListener("mouseup", up);
  }, [onGestureStart, onGestureMove, onGestureEnd]);

  // Wheel navigation (desktop / trackpad)
  const wheelAccum = useRef(0);
  const handleWheel = useCallback((e: WheelEvent) => {
    wheelAccum.current += e.deltaY;
    if (Math.abs(wheelAccum.current) > 40) {
      navigate(wheelAccum.current > 0 ? 1 : -1);
      wheelAccum.current = 0;
    }
  }, [navigate]);

  useEffect(() => {
    const el = rootRef.current;
    if (!el) return;
    el.addEventListener("wheel", handleWheel, { passive: true });
    return () => el.removeEventListener("wheel", handleWheel);
  }, [handleWheel]);

  // Any card change lands the pull wrapper back at rest.
  useEffect(() => { setPull(0, true); }, [currentIndex, setPull]);

  const styleFor = (index: number) => {
    const diff = index - currentIndex;
    if (diff === 0) return { y: 0, scale: 1, opacity: 1, zIndex: 5, rotateX: 0 };
    if (diff === -1) return { y: -Math.round(cardHeight * 0.34), scale: 0.86, opacity: 0.5, zIndex: 4, rotateX: 8 };
    if (diff === -2) return { y: -Math.round(cardHeight * 0.58), scale: 0.74, opacity: 0.22, zIndex: 3, rotateX: 14 };
    if (diff === 1) return { y: Math.round(cardHeight * 0.34), scale: 0.86, opacity: 0.5, zIndex: 4, rotateX: -8 };
    if (diff === 2) return { y: Math.round(cardHeight * 0.58), scale: 0.74, opacity: 0.22, zIndex: 3, rotateX: -14 };
    return { y: diff > 0 ? cardHeight : -cardHeight, scale: 0.6, opacity: 0, zIndex: 0, rotateX: diff > 0 ? -20 : 20 };
  };
  const isVisible = (index: number) => Math.abs(index - currentIndex) <= 2;

  return (
    <div ref={rootRef} className={"relative flex w-full items-center justify-center overflow-visible " + (className ?? "")}>
      <div
        ref={cardAreaRef}
        className="relative flex items-center justify-center"
        style={{ perspective: "1200px", width: cardWidth, height: cardHeight, touchAction: "none" }}
        onMouseDown={handleMouseDown}
      >
        {items.map((item, index) => {
          if (!isVisible(index)) return null;
          const st = styleFor(index);
          const isCurrent = index === currentIndex;
          return (
            <motion.div
              key={item.id}
              className="absolute"
              animate={{ y: st.y, scale: st.scale, opacity: st.opacity, rotateX: st.rotateX, zIndex: st.zIndex }}
              transition={{ type: "spring", stiffness: 300, damping: 30, mass: 1 }}
              style={{ transformStyle: "preserve-3d", zIndex: st.zIndex, width: cardWidth, height: cardHeight }}
            >
              <div ref={isCurrent ? pullRef : undefined} className="h-full w-full">
                <div
                  className="relative h-full w-full overflow-hidden rounded-[28px] bg-card ring-1 ring-border/50"
                  style={{
                    boxShadow: isCurrent
                      ? "0 26px 50px -12px hsl(var(--foreground) / 0.18), 0 0 0 1px hsl(var(--foreground) / 0.04)"
                      : "0 12px 30px -12px hsl(var(--foreground) / 0.12)",
                  }}
                >
                  <div className={"h-full w-full overflow-y-auto overscroll-contain " + (isCurrent ? "" : "pointer-events-none")}>
                    {item.node}
                  </div>
                  {!isCurrent && <div className="absolute inset-0 bg-background/20" />}
                </div>
              </div>
            </motion.div>
          );
        })}
      </div>

      {/* Navigation dots */}
      <div className="absolute right-1 top-1/2 flex -translate-y-1/2 flex-col gap-2 z-10">
        {items.map((_, index) => (
          <button
            key={index}
            onClick={() => setCurrentIndex(index)}
            className={"w-2 rounded-full transition-all duration-300 " + (index === currentIndex ? "h-6 bg-foreground/70" : "h-2 bg-foreground/25 hover:bg-foreground/40")}
            aria-label={`Go to card ${index + 1}`}
          />
        ))}
      </div>
    </div>
  );
}
